import React, { useCallback, useEffect, useRef, useState, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from "stories/components/Notification"

import { Typography, Button, Popover, Modal } from "stories/components";
import { httpDownloadFile } from "services/files"
import { httpDownloadReceiptPdf } from "services/accounts"
import { httpGetFundValuationReportAttachment } from "services/fund_valuation_reports"
import { logError } from "services/logger"

import ImageWithPolygons from 'components/ImageWithPolygons/ImageWithPolygons';
import { getMimeType, isImage, isPdf, isPreviewable } from './fileTypes';

// The PDF viewer and its worker are heavy; keep them out of the main bundle.
const PdfPreview = lazy(() => import('./PdfPreview'));

const FileDownloadButton = ({ fileId, fileName, params, className, popoverTitle, variant, showPreview=false, fileModel="File", polygons=null}) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { notify } = useNotification();
  const { t } = useTranslation();

  // Blob URLs stay alive until revoked. The previous version never revoked any, so a
  // previewed document stayed in browser memory for the life of the tab and was
  // re-fetched on every open. (Sovellon #2260, finding F-18.)
  const objectUrlRef = useRef(null);

  const releaseObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setFileUrl(null);
  }, []);

  useEffect(() => releaseObjectUrl, [releaseObjectUrl]);
  useEffect(() => { releaseObjectUrl(); }, [fileId, fileName, releaseObjectUrl]);

  const fetchFile = useCallback(() => {
    if (fileModel === "AccountLicensePayment") {
      return httpDownloadReceiptPdf(fileId, { payment_id: fileId });
    }
    if (fileModel === "FundValuationReport") {
      return httpGetFundValuationReportAttachment(fileId);
    }
    return httpDownloadFile(fileId, params);
  }, [fileId, fileModel, params]);

  const previewable = isPreviewable(fileName);

  const handlePreview = () => {
    setModalOpen(true);
    if (!previewable || objectUrlRef.current) {
      return;
    }
    setLoading(true);
    fetchFile()
      .then((response) => {
        if (!response) {
          throw new Error('Empty response');
        }
        const blob = new Blob([response.data], { type: getMimeType(fileName) });
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setFileUrl(url);
      })
      .catch((error) => {
        logError('File preview failed', error);
        notify({ title: t("file_preview_failed", "Esikatselu epäonnistui"), icon: "fas fa-file" });
      })
      .finally(() => setLoading(false));
  };

  const handleDownload = () => {
    notify({ title: t("file_download_in_progress", "Ladataan tiedostoa..."), icon: "fas fa-file-export" });
    fetchFile()
      .then((response) => {
        const blob = new Blob([response.data], { type: getMimeType(fileName) });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.style.display = "none";
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      })
      .catch((error) => {
        logError('File download failed', error);
        notify({ title: t("file_download_failed", "Lataus epäonnistui"), icon: "fas fa-file-export" });
      });
  };

  const toggleModal = () => {
    setModalOpen((open) => {
      if (open) {
        releaseObjectUrl();
      }
      return !open;
    });
  };

  const renderPreview = () => {
    if (!previewable) {
      // The button is still offered for every type: a missing button reads as a bug,
      // while this says what will happen instead. (Sovellon #2260, batch D.)
      return (
        <div className="text-center py-4">
          <Typography variant="h5" className="text-muted mb-3">
            {t("file_preview_unsupported", "Tätä tiedostotyyppiä ei esikatsella selaimessa.")}
          </Typography>
          <Button variant="primary" onClick={handleDownload}>
            {t("file_preview_download_instead", "Lataa tiedosto")}
          </Button>
        </div>
      );
    }

    if (loading || !fileUrl) {
      return <div className="p-4 text-center text-muted">{t("loading", "Ladataan...")}</div>;
    }

    if (isImage(fileName)) {
      if (polygons && polygons.length > 0) {
        return (
          <ImageWithPolygons fileURL={fileUrl} polygons={polygons} fileId={fileId} fileName={fileName} />
        );
      }
      return (
        <div className="text-center">
          <img src={fileUrl} alt={fileName} style={{ maxWidth: '100%', maxHeight: '650px' }} />
        </div>
      );
    }

    if (isPdf(fileName)) {
      return (
        <Suspense fallback={<div className="p-4 text-center text-muted">{t("loading", "Ladataan...")}</div>}>
          <PdfPreview fileUrl={fileUrl} />
        </Suspense>
      );
    }

    return null;
  };

  return (
    <div className="d-flex">
      {showPreview && (
        <>
          <Button
            id={`preview-${fileId}`}
            className={className ? className : "text-white font-weight-light rounded-circle custom-xs-button-input b-0"}
            style={{ position: "relative", zIndex: 0 }}
            variant={variant ? variant : "lighter"}
            onClick={handlePreview}
          >
            <i className="fas fa-eye text-primary" />
          </Button>
          <Popover placement="left" target={`preview-${fileId}`}>
            <Typography className="text-dark mb-1" variant="small">
              {t("file_preview_show", "Esikatsele")}
            </Typography>
          </Popover>
        </>
      )}

      <Button
        id={`downloadFile-${fileId}`}
        className={className ? className : "text-white font-weight-light rounded-circle custom-xs-button-input b-0"}
        style={{ position: "relative", zIndex: 0 }}
        variant={variant ? variant : "lighter"}
        onClick={handleDownload}
      >
        <i className="fas fa-file-download text-primary" />
      </Button>
      <Popover placement="left" target={`downloadFile-${fileId}`}>
        <Typography className="text-dark mb-1" variant="small">
          {popoverTitle ? popoverTitle : t("file_download_download", 'Lataa')}
        </Typography>
      </Popover>

      <Modal title={fileName} isOpen={modalOpen} toggleModal={toggleModal} size="modal-xl">
        {modalOpen ? renderPreview() : null}
      </Modal>
    </div>
  );
};

export default FileDownloadButton;
